/**
 * Helpers centralizados de visibilidad/disponibilidad de acciones (RBAC/ACL v2).
 *
 * El backend es SIEMPRE la autoridad final. Estas funciones solo deciden si la
 * UI debe OCULTAR o DESHABILITAR un control; nunca otorgan acceso real.
 *
 * Dos planos de decisión:
 * - Menú: `canShowMenu(moduleCode)` ≡ nivel de módulo VIEW (hasModuleLevel).
 * - Acción concreta: `canShowAction(actionCode, resourceRef?, ctx?)`, que evalúa
 *   en orden: (1) nivel de módulo, (2) permiso atómico, (3) ACL mínima sobre el
 *   recurso, (4) estado del recurso (firmado/bloqueado/legal-hold/archivado/
 *   cerrado), (5) confidencialidad, (6) reglas especiales (export/share/IA/
 *   descarga), (7) origen del acceso efectivo.
 *
 * Las reglas viven en `ACTION_RULES` (catálogo declarativo). Para evaluar la
 * capa ACL/estado, el llamador inyecta la decisión ya cargada (p. ej. desde
 * `useResourceAccess`) mediante `ctx.access`; sin contexto de recurso solo se
 * evalúan módulo + permiso.
 */
import type { RbacLevel } from "@/config/rbac";
import type { AclAccessRole, AclOrigin } from "@/services/acl";

/** Capacidad RBAC consultada contra el token vigente. */
export interface AccessCapabilities {
  hasModuleLevel: (moduleCode: string, level: RbacLevel) => boolean;
  hasPermission: (code: string) => boolean;
  isSuperadmin: boolean;
  /** True si el actor es Abogado Director (rol de tenant). */
  isLegalDirector?: boolean;
}

/** Estado del recurso que puede limitar una acción aunque haya permisos. */
export interface ResourceStateContext {
  /** Documento firmado (e-sign cerrado). */
  signed?: boolean;
  /** Recurso bloqueado para edición. */
  locked?: boolean;
  /** Retención legal activa. */
  legalHold?: boolean;
  /** Archivado. */
  archived?: boolean;
  /** Matter cerrado. */
  closed?: boolean;
  /** Versión histórica (no editable). */
  historical?: boolean;
  /** Marcado confidencial. */
  confidential?: boolean;
}

/** Decisión de acceso efectivo sobre el recurso (de `useResourceAccess`). */
export interface EffectiveAccessContext {
  accessRole: AclAccessRole | null;
  origin: AclOrigin;
  canManage: boolean;
  confidential?: boolean;
  requiresReason?: boolean;
}

export interface ActionContext {
  /** Acceso efectivo sobre el recurso (si la acción opera sobre uno). */
  access?: EffectiveAccessContext;
  /** Estado del recurso (firmado, bloqueado, etc.). */
  state?: ResourceStateContext;
}

/** Resultado granular de la evaluación de una acción. */
export interface ActionDecision {
  /** Mostrar el control en la UI. */
  visible: boolean;
  /** El control se muestra pero deshabilitado (falta estado/ACL puntual). */
  disabled: boolean;
  /** La acción exige capturar un motivo antes de confirmar. */
  requiresReason: boolean;
  /** Razón legible (para tooltip) cuando está oculto/deshabilitado. */
  reason?: string;
}

/** Orden jerárquico de roles ACL para comparar mínimos. */
const ACL_RANK: Record<AclAccessRole, number> = {
  LECTOR: 1,
  COLABORADOR: 2,
  RESPONSABLE: 3,
};

/** Reglas especiales reconocidas por acción. */
export type ActionSpecial =
  | "download"
  | "export"
  | "share-external"
  | "edit-document"
  | "ai"
  | "scoring";

/** Definición declarativa de una acción gated. */
export interface ActionRule {
  /** Módulo RBAC al que pertenece la acción. */
  module: string;
  /** Nivel de módulo mínimo (default VIEW). */
  level?: RbacLevel;
  /** Permiso atómico requerido (además del nivel de módulo). */
  permission?: string;
  /** Permiso atómico alternativo aceptado (legacy/equivalente). */
  permissionAny?: readonly string[];
  /** ACL mínima sobre el recurso, si la acción opera sobre uno. */
  minAcl?: AclAccessRole;
  /** Estados del recurso que BLOQUEAN la acción (deshabilita). */
  stateGuard?: ReadonlyArray<keyof ResourceStateContext>;
  /** Regla especial adicional (export/share/IA/descarga/scoring). */
  special?: ActionSpecial;
  /** El recurso confidencial exige motivo para ejecutar. */
  reasonOnConfidential?: boolean;
}

/**
 * Catálogo de acciones gated (RBAC/ACL v2). Centraliza módulo + nivel + permiso
 * + ACL mínima + guardas de estado + reglas especiales por acción.
 */
export const ACTION_RULES = {
  // ---- Clientes ----
  "client.create": {
    module: "clients",
    level: "INTERACT",
    permission: "client.create",
  },
  "client.update": {
    module: "clients",
    level: "INTERACT",
    permission: "client.update",
    minAcl: "COLABORADOR",
  },
  "client.delete": {
    module: "clients",
    level: "DELETE",
    permission: "client.delete",
    minAcl: "RESPONSABLE",
  },
  "client.share": {
    module: "clients",
    level: "INTERACT",
    permission: "clients.team.manage",
    minAcl: "RESPONSABLE",
  },

  // ---- Matters ----
  "matter.create": {
    module: "matters",
    level: "INTERACT",
    permissionAny: ["matters.create", "projects.create"],
  },
  "matter.update": {
    module: "matters",
    level: "INTERACT",
    permissionAny: ["matters.update", "projects.update"],
    minAcl: "COLABORADOR",
  },
  "matter.close": {
    module: "matters",
    level: "DELETE",
    permissionAny: [
      "matters.close",
      "projects.archive",
      "project.archive.write",
    ],
    minAcl: "RESPONSABLE",
    stateGuard: ["closed", "archived"],
  },

  // ---- Documentos ----
  "document.ai.compose": {
    module: "documents",
    level: "INTERACT",
    permissionAny: [
      "document.upload",
      "document.upload.execute",
      "document.ai.compose",
      "ai.document.compose",
    ],
    minAcl: "COLABORADOR",
    special: "ai",
    stateGuard: ["closed", "archived"],
  },
  "document.upload": {
    module: "documents",
    level: "INTERACT",
    permissionAny: ["document.upload", "document.upload.execute"],
    minAcl: "COLABORADOR",
    stateGuard: ["closed", "archived"],
  },
  "document.edit": {
    module: "documents",
    level: "INTERACT",
    permissionAny: ["documents.edit", "document.edit", "document.update"],
    minAcl: "COLABORADOR",
    special: "edit-document",
    stateGuard: ["signed", "locked", "historical", "archived"],
  },
  "document.download": {
    module: "documents",
    permissionAny: ["document.download", "document.download.execute"],
    minAcl: "LECTOR",
    special: "download",
  },
  "document.export": {
    module: "documents",
    permissionAny: ["document.export.execute", "document.export"],
    minAcl: "LECTOR",
    special: "export",
    reasonOnConfidential: true,
  },
  "document.share.external": {
    module: "documents",
    level: "INTERACT",
    permissionAny: ["documents.share.external", "document.share.external"],
    minAcl: "RESPONSABLE",
    special: "share-external",
    reasonOnConfidential: true,
  },
  "document.reviewers.manage": {
    module: "documents",
    level: "INTERACT",
    permissionAny: ["document.reviewers.manage", "documents.reviewers.manage"],
    minAcl: "RESPONSABLE",
  },

  // ---- Riesgos ----
  "risk.create": {
    module: "risks",
    level: "INTERACT",
    permissionAny: ["risk.create", "risk.write"],
  },
  "risk.scoring.change": {
    module: "risks",
    level: "INTERACT",
    permissionAny: ["risk.scoring.change", "risk.scoring.write"],
    special: "scoring",
  },

  // ---- Administración ----
  "user.create": {
    module: "users-roles",
    level: "INTERACT",
    permissionAny: ["users.create", "users.invite"],
  },
  "rbac.matrix.edit": {
    module: "users-roles",
    level: "INTERACT",
    permissionAny: ["roles.manage", "rbac.matrix.edit"],
  },
} as const satisfies Record<string, ActionRule>;

export type ActionCode = keyof typeof ACTION_RULES;

/**
 * Visibilidad de un ítem de menú: equivale a tener nivel VIEW del módulo.
 * El SUPERADMIN ve todo (lo resuelve `hasModuleLevel`).
 */
export function canShowMenu(
  caps: AccessCapabilities,
  moduleCode: string,
): boolean {
  return caps.hasModuleLevel(moduleCode, "VIEW");
}

function meetsAcl(
  access: EffectiveAccessContext | undefined,
  caps: AccessCapabilities,
  minAcl: AclAccessRole,
): boolean {
  // Sin contexto de recurso cargado no podemos negar por ACL: el backend
  // sigue siendo la autoridad. Solo evaluamos ACL cuando hay decisión.
  if (!access) return true;
  if (caps.isSuperadmin) return true;
  // Gestores efectivos (responsable/global scope con manage) cumplen cualquier mínimo.
  if (access.canManage) return true;
  if (!access.accessRole) return false;
  return ACL_RANK[access.accessRole] >= ACL_RANK[minAcl];
}

function hasResponsibleAclOverride(
  access: EffectiveAccessContext | undefined,
  caps: AccessCapabilities,
): boolean {
  if (!access) return false;
  if (caps.isSuperadmin) return true;
  return access.canManage || access.accessRole === "RESPONSABLE";
}

function blockingState(
  state: ResourceStateContext | undefined,
  guards: ReadonlyArray<keyof ResourceStateContext> | undefined,
): keyof ResourceStateContext | null {
  if (!state || !guards) return null;
  return guards.find((key) => state[key] === true) ?? null;
}

const STATE_REASON: Record<keyof ResourceStateContext, string> = {
  signed: "El documento está firmado.",
  locked: "El recurso está bloqueado.",
  legalHold: "Hay una retención legal activa.",
  archived: "El recurso está archivado.",
  closed: "El asunto está cerrado.",
  historical: "Estás viendo una versión histórica.",
  confidential: "El recurso es confidencial.",
};

/**
 * Evaluación granular de una acción. Devuelve visibilidad, deshabilitado y si
 * exige motivo. Pensado para gobernar `hidden`/`disabled` de un botón.
 */
export function evaluateAction(
  actionCode: ActionCode,
  caps: AccessCapabilities,
  ctx: ActionContext = {},
): ActionDecision {
  const rule = ACTION_RULES[actionCode] as ActionRule;
  const visibleHidden: ActionDecision = {
    visible: false,
    disabled: true,
    requiresReason: false,
  };

  // SUPERADMIN ve todo; el ESTADO (firmado/bloqueado/etc.) aún limita escritura.
  const isSuper = caps.isSuperadmin;
  const aclOverride = Boolean(
    rule.minAcl && hasResponsibleAclOverride(ctx.access, caps),
  );

  // (1) nivel de módulo.
  if (
    !isSuper &&
    !aclOverride &&
    !caps.hasModuleLevel(rule.module, rule.level ?? "VIEW")
  ) {
    return { ...visibleHidden, reason: "Sin acceso al módulo." };
  }

  // (2) permiso atómico (cualquiera de los aceptados).
  if (!isSuper && !aclOverride) {
    const permList = [
      ...(rule.permission ? [rule.permission] : []),
      ...(rule.permissionAny ?? []),
    ];
    if (permList.length > 0 && !permList.some((p) => caps.hasPermission(p))) {
      return { ...visibleHidden, reason: "Sin permiso para la acción." };
    }
  }

  // (6/7) reglas especiales.
  // Compartir externo: el permiso `documents.share.external` (validado arriba)
  // es por defecto exclusivo de SUPERADMIN / Abogado Director en la matriz RBAC;
  // así el caso base queda restringido sin lógica extra aquí. El motivo se exige
  // más abajo vía `reasonOnConfidential`.
  if (rule.special === "scoring" && caps.isLegalDirector && !isSuper) {
    // Cambiar scoring NO está disponible para Abogado Director.
    return {
      ...visibleHidden,
      reason: "El Abogado Director no modifica el scoring de riesgo.",
    };
  }

  // (3) ACL mínima sobre el recurso.
  if (rule.minAcl && !meetsAcl(ctx.access, caps, rule.minAcl)) {
    return { ...visibleHidden, reason: "Acceso insuficiente al recurso." };
  }

  // A partir de aquí la acción es VISIBLE. Determinamos disabled/reason.
  // (4) estado del recurso: deshabilita aunque el actor sea Abogado Director.
  const blocked = blockingState(ctx.state, rule.stateGuard);
  if (blocked) {
    return {
      visible: true,
      disabled: true,
      requiresReason: false,
      reason: STATE_REASON[blocked],
    };
  }

  // (5) confidencialidad → motivo obligatorio para export/share confidencial.
  const confidential = Boolean(
    ctx.state?.confidential ?? ctx.access?.confidential,
  );
  const requiresReason = Boolean(
    rule.reasonOnConfidential &&
    (confidential || ctx.access?.requiresReason === true),
  );

  return { visible: true, disabled: false, requiresReason };
}

/**
 * Conveniencia booleana: ¿se muestra la acción? (no necesariamente habilitada).
 * Para distinguir oculto de deshabilitado usa `evaluateAction`.
 */
export function canShowAction(
  actionCode: ActionCode,
  caps: AccessCapabilities,
  ctx: ActionContext = {},
): boolean {
  return evaluateAction(actionCode, caps, ctx).visible;
}

/** ¿La acción está disponible (visible y habilitada)? */
export function canDoAction(
  actionCode: ActionCode,
  caps: AccessCapabilities,
  ctx: ActionContext = {},
): boolean {
  const decision = evaluateAction(actionCode, caps, ctx);
  return decision.visible && !decision.disabled;
}
